import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { useListClickHandlers } from './functions/useListClickHandlers';
import { resolveDetailPageUrl } from './functions/detailPageUrl';
import ListContext from './ListContext';
import { ListRowProps, ListRowType } from 'types/ListTypes';
import { getDeletionLabel } from 'services/utils/deletionUtils';
import ListSelectionCheckbox from './ListSelectionCheckbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faGripLines } from '@fortawesome/free-solid-svg-icons';
import { faTurnLeft } from '@fortawesome/pro-solid-svg-icons';

const ListRow = <T extends ListRowType>({
    row, gridClass, query, objectName, columns, isDraggable, showDeleteOrDeactivate, showDeleteButton,
    onRowClick, detailPageUrl, detailModal, detailModalName, releaseViewModal, wizardModal, formModal, editModal, modalSize, detailModalSize,
    provided = null, snapshot = null, showRevertButton, showFillerColumn, allowEditingInactiveRows, bulkActions, isRowSelected, onToggleRowSelect
}: ListRowProps<T>) => {
    const { t } = useTranslation();
    const hasRightCheck = useAllowedRight;
    const { userLocale, userTimezone } = useSettings();
    const { refetchList } = useContext(ListContext);
    const { handleRowClick, handleDelete, handleRevert } = useListClickHandlers({
        apiObject: query.object, objectName, detailPageUrl, refetchList, onRowClick, detailModal, detailModalName, releaseViewModal, wizardModal, 
        formModal, editModal, modalSize, detailModalSize, userLocale, userTimezone
    });

    // Set the style for a draggable row. If the row is currently being dragged, the background color is changed
    const setDragStyle = (isDragging: boolean, draggableStyle: React.CSSProperties | undefined): React.CSSProperties => {
        return {    
            background: isDragging ? '#f4f4f4' : '',
            ...draggableStyle
        };
    }

    // Set the row props and style for draggable or non draggable lists
    const rowStyle = { gridTemplateColumns: gridClass, ...isDraggable ? setDragStyle(snapshot.isDragging, provided.draggableProps.style) : {} }

    // Determine to make the row not clickable
    const isRowClickable = (row: ListRowType, showRevertButton: boolean = false, allowEditingInactiveRows: boolean = false) => {
        if (allowEditingInactiveRows) {
            return true;
        }
        
        if (showRevertButton) {
            const isDeleted = row.deleted === true;
            const isInactive = row.is_active === false;

            return !(isDeleted || isInactive);
        } else {
            return true;
        }
    };
        
    // The detail page path of the row, rendered as a real link in every cell so the
    // browser offers open in new tab on a right click, a ctrl click or a middle click
    const rowLink = onRowClick === 'detailPage' && isRowClickable(row, showRevertButton, allowEditingInactiveRows)
        ? resolveDetailPageUrl(detailPageUrl, row)
        : null;

    // A plain left click on a cell link keeps the row behavior instead of a full page load,
    // runs in the capture phase so a stopped click inside a cell can never navigate
    const handleRowLinkClickCapture = (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
            event.preventDefault();
        }
    };

    // Determine whether a click is a modifier click the browser handles through the cell link
    const isModifierClick = (event: React.MouseEvent) => event.ctrlKey || event.metaKey || event.shiftKey;

    return (
        <tr {...(isDraggable ? { ...provided.draggableProps, ...provided.dragHandleProps, ref: provided.innerRef } : {} )} 
            style={rowStyle} 
            id={row.id.toString()}
            key={row.id}
            onClick={(event) => {
                if (rowLink && isModifierClick(event)) {
                    return;
                }
                if (isRowClickable(row, showRevertButton, allowEditingInactiveRows)) {
                    handleRowClick(row.id, row);
                }
            }}
        >
            {bulkActions && (
                // Shows a selection checkbox, stops propagation so it does not open the row
                <td className='checkbox-column' onClick={(e) => e.stopPropagation()}>
                    <ListSelectionCheckbox
                        id={`list-select-${row.id}`}
                        checked={!!isRowSelected}
                        onChange={() => onToggleRowSelect && onToggleRowSelect(row.id)}
                    />
                </td>
            )}
            {isDraggable && (
                // Shows a drag icon if the list is draggable
                <td className='tooltip-column'>
                    <span className='list-drag-icon tooltip-icon'>
                        <FontAwesomeIcon icon={faGripLines} />
                        <span className="tooltip">{t('general.reorder')}</span>
                    </span>
                </td>
            )}
            {columns && columns.map((column) => (
                // Renders the row columns
                <td key={`${row.id}-${column.field}`} className={
                    column.customClass
                        ? typeof column.customClass === 'function'
                            ? column.customClass(row[column.field], row) || ''
                            : column.customClass
                        : ''
                    }
                >
                    {rowLink ? (
                        // Wraps the cell content in the row link so the browser can open the page in a new tab
                        <a href={rowLink} className='data row-link' draggable={false} onClickCapture={handleRowLinkClickCapture}>
                            {column.render 
                                ? column.render(row[column.field], row as ListRowType) 
                                : column.translationString ? t(row[column.field]) 
                                : row[column.field]
                            }
                        </a>
                    ) : (
                        <div className='data'>
                            {column.render 
                                ? column.render(row[column.field], row as ListRowType) 
                                : column.translationString ? t(row[column.field]) 
                                : row[column.field]
                            }
                        </div>
                    )}
                </td>
            ))}
            {(showDeleteButton || showRevertButton) && (
                // Shows a delete or revert button in lists with active or non-deleted query params, don't show it for users without delete rights
                <td className='delete-column'>
                    {showDeleteButton ? (
                        hasRightCheck('can_delete') &&
                            <div className='list-delete-icon tooltip-icon'>
                                <FontAwesomeIcon
                                    icon={faTrash}
                                    className='fas fa-trash'
                                    style={{ cursor: 'pointer' }}
                                    onClick={(e) => { e.stopPropagation(); handleDelete(row.id, showDeleteOrDeactivate);}} />
                                <span className="tooltip">{t(getDeletionLabel(showDeleteOrDeactivate))}</span>
                            </div>
                    ) : (
                        hasRightCheck('can_delete') &&
                            <div className='list-revert-icon tooltip-icon'>
                                <FontAwesomeIcon
                                    icon={faTurnLeft}
                                    className='fas fa-trash'
                                    style={{ cursor: 'pointer' }}
                                    onClick={(e) => { e.stopPropagation(); handleRevert(row.id, showDeleteOrDeactivate);}} />
                                <span className="tooltip">{t('general.revert')}</span>
                            </div>
                    )}

                </td>
            )}
            {showFillerColumn && (
                // Empty trailing cell absorbing the leftover space in the full width layout
                <td></td>
            )}
        </tr>
    )
}

export default ListRow;